import { Modal, Table, Badge, Alert, Button, Container, Row, Col } from "react-bootstrap";
import { useContext, useEffect, useRef, useState } from "react";
import { format } from 'date-fns';
import { useAppSelector } from "../../../../../redux/hooks";
import { ReducerEnum, setClientPortalModalType } from "../../../../../redux/reducer";
import { ClientPortalModalType } from "../../../types";
import { useClientInformation } from '../../../hooks/useClientInformation';
import { useRetainerInformationQuery } from '../../../hooks/useRetainerInformationQuery';
import StateContext from '../../../context/State';
import PortalService from '../../../services/portal.service';
import { buildRetainerPdfBlobUrl } from './RetainerPdfTemplate';
import { isAgreementProvince } from "../../../../../../../share/utils/agreementProvince";


export type ClientRetainerTaskModalProps = {
    dispatch: (arg0: {
        type: ReducerEnum;
        payload: any;
        key?: string;
        extraPayload?: Record<string, string>;
    }) => void;
}

const ClientRetainerTaskModal = (props: ClientRetainerTaskModalProps) => {
    const { state } = useContext(StateContext);
    const portalService = new PortalService(state);

    const show: boolean = useAppSelector((s) => s.ClientPortalState?.ModalType === ClientPortalModalType.CLIENT_RETAINER);
    // MB/SK label this "Agreement" (UI only).
    const agreementMode = isAgreementProvince(useAppSelector((s) => s.SessionInfo.appProvinceShort));
    const noun = agreementMode ? 'Agreement' : 'Retainer';

    const { data: clients = [] } = useClientInformation(show);
    const { isRetainerRequired, retainerConfig } = useRetainerInformationQuery(show);

    const isPdfRetainer = Boolean(retainerConfig?.UploadRetainer);
    const isTextRetainer = Boolean(retainerConfig?.TextBoxRetainerRequired);
    const textRetainerContent: string = retainerConfig?.TextBoxRetainerString ?? '';
    const hasRenderableContent = isPdfRetainer || (isTextRetainer && textRetainerContent.trim().length > 0);
    const anyAcknowledged = clients.some(client => Boolean(client.RetainerAcknowledged));
    const actionsDisabled = !anyAcknowledged;

    const signaturesKey = clients
        .filter(c => Boolean(c.RetainerAcknowledged) && c.RetainerAcknowledgedDateTime)
        .map(c => `${c.Name}|${c.RetainerAcknowledgedDateTime}`)
        .join(';');

    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // PDF mode: fetch the lawyer-uploaded PDF from backend.
    // Does NOT depend on signaturesKey — signatures don't change the PDF content.
    useEffect(() => {
        if (!show || !isPdfRetainer || !anyAcknowledged) {
            return;
        }

        let cancelled = false;
        let createdUrl: string | null = null;

        portalService.getRetainerBlobUrl(state.portalId)
            .then(url => {
                if (cancelled) {
                    window.URL.revokeObjectURL(url);
                    return;
                }
                createdUrl = url;
                setBlobUrl(url);
            })
            .catch(e => console.error('Unable to load retainer:', e));

        return () => {
            cancelled = true;
            if (createdUrl) {
                window.URL.revokeObjectURL(createdUrl);
            }
            setBlobUrl(null);
            setShowPreview(false);
            setIframeLoaded(false);
        };
    }, [show, isPdfRetainer, anyAcknowledged, state.portalId]);

    // Text mode: generate PDF locally from text + signatures.
    // Depends on signaturesKey so new signatures cause regeneration.
    useEffect(() => {
        if (!show || !isTextRetainer || !anyAcknowledged || textRetainerContent.trim().length === 0) {
            return;
        }

        const signatures = clients
            .filter(c => Boolean(c.RetainerAcknowledged) && c.RetainerAcknowledgedDateTime)
            .map(c => ({
                name: c.Name,
                signedAt: c.RetainerAcknowledgedDateTime
            }));
        const url = buildRetainerPdfBlobUrl(textRetainerContent, signatures, agreementMode ? 'Agreement' : 'Retainer Agreement');
        setBlobUrl(url);

        return () => {
            window.URL.revokeObjectURL(url);
            setBlobUrl(null);
            setShowPreview(false);
            setIframeLoaded(false);
        };
    }, [show, isTextRetainer, textRetainerContent, anyAcknowledged, signaturesKey]);

    const handlePreview = () => {
        if (!blobUrl) return;
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${noun} Preview</title>
                <style>
                    html, body { margin: 0; padding: 0; height: 100%; overflow: hidden; }
                    iframe { width: 100%; height: 100%; border: 0; }
                </style>
            </head>
            <body>
                <iframe src="${blobUrl}#toolbar=0"></iframe>
            </body>
            </html>
        `;

        const retainerPreviewWindow = window.open(' ', '_blank');
        if (retainerPreviewWindow) {
            retainerPreviewWindow.document.write(htmlContent);
            retainerPreviewWindow.document.close();
        }
        else {
            alert(`Allow pop-ups to preview the ${noun.toLowerCase()} in a new window.`);
        }
    };

    // const handleDownload = () => {
    //     if (!blobUrl) return;
    //     const link = document.createElement('a');
    //     link.href = blobUrl;
    //     link.setAttribute('download', 'retainer.pdf');
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    // };

    // const handlePrint = () => {
    //     const iframe = iframeRef.current;
    //     if (!iframe) return;
    //     try {
    //         iframe.contentWindow?.focus();
    //         iframe.contentWindow?.print();
    //     } catch (err) {
    //         console.error('Print failed:', err);
    //     }
    // };

    return (
        <>
            <Modal
                show={show}
                onHide={() => { props.dispatch(setClientPortalModalType(ClientPortalModalType.NONE)) }}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                className="modal-list"
            >
                <Modal.Header closeButton>
                    <Container className="gx-0">
                        <Row className="gx-0">
                            <Col>
                                <Modal.Title id="contained-modal-title-vcenter">Client {noun} Status</Modal.Title>
                            </Col>
                            {hasRenderableContent && (
                                <Col className="align-right">
                                    <Button
                                        variant="outline-primary"
                                        title={`Preview ${noun}`}
                                        onClick={handlePreview}
                                        disabled={actionsDisabled || !blobUrl}
                                        style={{ marginRight: '15px' }}
                                    >
                                        <i className="fa-regular fa-eye"></i> Preview
                                    </Button>
                                    {/*
                                    <Button
                                        variant="outline-primary"
                                        title="Download Retainer"
                                        onClick={handleDownload}
                                        disabled={actionsDisabled || !blobUrl}
                                        style={{ marginRight: '5px' }}
                                    >
                                        <i className="fa-regular fa-download"></i> Download
                                    </Button>
                                    <Button
                                        title="Print Retainer"
                                        onClick={handlePrint}
                                        disabled={actionsDisabled || !blobUrl || !iframeLoaded}
                                        style={{ marginRight: '15px' }}
                                    >
                                        <i className="fa-regular fa-print"></i> Print
                                    </Button>
                                    */}
                                </Col>
                            )}
                        </Row>
                    </Container>
                </Modal.Header>

                <Modal.Body>
                    {isRetainerRequired ?
                        (
                            <>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Client</th>
                                            <th>Status</th>
                                            <th>Date Signed</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {clients ?
                                            <>
                                                {clients.map((client, index) => (
                                                    <tr key={index}>
                                                        <td>{client.Name}</td>
                                                        <td>{client.RetainerAcknowledged ? <Badge bg="success" text="dark" pill>Signed</Badge> : <Badge bg="secondary" text="dark" pill>Not Signed</Badge>} </td>
                                                        <td>
                                                            {client.RetainerAcknowledged && client.RetainerAcknowledgedDateTime ? (
                                                                <Badge pill text="dark" bg="info">
                                                                    {format(new Date(client.RetainerAcknowledgedDateTime), "MMMM do, yyyy 'at' h:mm a")}
                                                                </Badge>
                                                            ) : (
                                                                ''
                                                            )}
                                                        </td>
                                                    </tr>
                                                )

                                                )}
                                            </>
                                            :
                                            <></>
                                        }
                                    </tbody>
                                </Table>

                                {/*
                                {blobUrl && (
                                    <iframe
                                        ref={iframeRef}
                                        src={`${blobUrl}#toolbar=0`}
                                        title="Retainer PDF"
                                        onLoad={() => setIframeLoaded(true)}
                                        style={{
                                            width: '100%',
                                            height: showPreview ? '600px' : '0',
                                            border: showPreview ? '1px solid #dee2e6' : 'none',
                                            display: 'block',
                                            marginTop: showPreview ? '15px' : '0'
                                        }}
                                    />
                                )}
                                */}
                            </>
                        )
                        :
                        (
                            <>
                                <Alert style={{margin:"20px"}}>{noun} not Required</Alert>
                            </>
                        )
                    }
                </Modal.Body>
            </Modal>
        </>
    );
}

export default ClientRetainerTaskModal;
